/**
 * MERK LEST NÅR SAKEN ÅPNES — FOR ALLE VARSELTYPER (punkt 111, KONTRAKT-B «B-mel»).
 *
 * Skuffene som viser en sak (tråden, byråets forespørsel, artistkortet) merker sine egne varsler
 * lest. Alle andre typer — oppgaven, vakta, konserten, kalenderhendelsen, «Mine konserter» — ble
 * bare merket når noen trykket på selve varselet. Kom man dit en annen vei, sto det igjen.
 *
 * Regelen er nå ÉN, og den står ett sted: står leseren på stedet et ulest varsel peker til
 * (`notificationRoute`, den samme som klokka navigerer med), er varselet lest. Det trengs ingen
 * kode per side, og en ny varseltype med en rute får det med seg av seg selv.
 *
 * Bare når det finnes uleste (tallet i klokka hentes uansett), og bare blant de nyeste: en lang hale
 * av gamle varsler er ikke grunn nok til å hente en liste på hver sidevisning.
 */
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useRouterState } from '@tanstack/react-router';
import { useEffect, useRef } from 'react';

import type { OrganizationKind } from '@tvitvi/shared';

import {
  clearObjectNotifications,
  unreadCountQueryOptions,
  unreadNotificationsQueryOptions,
} from '@/features/overview/queries';

import { notificationRoute, type NotificationRoute } from './notification-route';

/** Så mange uleste vurderes. Det samme taket varselfanen åpner med. */
const ARRIVAL_ROWS = 20;

/** Står adressen på målet? Stien med parametrene satt inn, og hver søkeparameter målet krever. */
export function routeMatches(
  route: NotificationRoute,
  location: { pathname: string; search: Record<string, unknown> },
): boolean {
  const path = route.to.replace(/\$([A-Za-z]+)/g, (_, name: string) => route.params?.[name] ?? '');
  const trim = (value: string): string => (value.length > 1 ? value.replace(/\/+$/, '') : value);
  if (trim(path) !== trim(location.pathname)) return false;
  return Object.entries(route.search ?? {}).every(
    ([key, value]) => String(location.search[key] ?? '') === value,
  );
}

/**
 * PEKER RUTEN PÅ SELVE TINGEN? Bare da er det å stå der det samme som å ha åpnet den. En liste over
 * hele årgangen, festivalen eller organisasjonen er ikke saken — «Ella har åpnet lenken» er ikke lest
 * fordi man tilfeldigvis står i personlista. Id-en må stå i stien eller i søket.
 */
const CONTAINERS: readonly string[] = ['edition', 'festival', 'organization'];

export function identifiesObject(
  route: NotificationRoute,
  row: { objectType: string | null; objectId: string | null },
): boolean {
  if (row.objectType === null || row.objectId === null) return false;
  if (CONTAINERS.includes(row.objectType)) return false;
  const values = [...Object.values(route.params ?? {}), ...Object.values(route.search ?? {})];
  return values.includes(row.objectId);
}

export function useClearNotificationsOnArrival(
  orgId: string,
  kind: OrganizationKind,
  /** Av i gjestemodus: varslene er hjemmets, adressen er husets som delte. */
  active = true,
): void {
  const queryClient = useQueryClient();
  const location = useRouterState({ select: (state) => state.location });
  const badge = useQuery(unreadCountQueryOptions(orgId));
  const unread = useQuery({
    ...unreadNotificationsQueryOptions(orgId, ARRIVAL_ROWS),
    enabled: active && orgId !== '' && (badge.data?.count ?? 0) > 0,
  });
  const cleared = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (!active) return;
    for (const row of unread.data?.items ?? []) {
      if (row.objectType === null || row.objectId === null) continue;
      const route = notificationRoute(row, kind);
      const here = {
        pathname: location.pathname,
        search: location.search as unknown as Record<string, unknown>,
      };
      if (route === null || !identifiesObject(route, row) || !routeMatches(route, here)) continue;
      const seen = row.id;
      if (cleared.current.has(seen)) continue;
      cleared.current.add(seen);
      clearObjectNotifications(queryClient, orgId, row.objectType, row.objectId);
    }
  }, [active, unread.data, location, kind, orgId, queryClient]);
}
